'use client'

import { useContext } from 'react'

import { Cell, Icon, Bubble } from '@vinted/web-ui'
import { LocationPinMap24 } from '@vinted/monochrome-icons'

import { useTranslate } from '@marketplace-web/shared/i18n'

import {
  DistanceUnitEnum,
  DropOffsPointType,
} from '@marketplace-web/conversation-shipping/conversation-shipping-data'
import {
  GetShippingLabelContext,
  useDropOffPointMapContext,
} from '@marketplace-web/shipping-services-provider/shipping-services-provider-feature'
import { AddressModel } from '@marketplace-web/address/address-data'

type Props = {
  onDropOffPointOpen: () => void
  address?: AddressModel | null
}

const DropOffPoints = ({ onDropOffPointOpen, address }: Props) => {
  const translate = useTranslate('conversation.get_shipping_label.modal')
  const { selectedDropOffPoint, nearbyDropOffPoints } = useDropOffPointMapContext()
  const {
    state: {
      labelOptions: { labelOptions },
      dropOffPointTypeSelection: { selectedDropOffPointType },
    },
  } = useContext(GetShippingLabelContext)

  const isDropOffMapEnabled = labelOptions?.dropOffMapEnabled
  const isHomePickupSelected = selectedDropOffPointType === DropOffsPointType.Home

  const getDistanceToNearestDropOffPoint = () => {
    if (!selectedDropOffPoint) return null

    const { distance, distanceUnit } = selectedDropOffPoint

    if (!distanceUnit) return null
    if (!distance && distance !== 0) return null

    if (distanceUnit === DistanceUnitEnum.Kilometers || distanceUnit === DistanceUnitEnum.Miles) {
      const roundedDistance = distance.toFixed(1)

      return `${roundedDistance} ${distanceUnit}`
    }

    const roundedDistance = Math.round(distance)

    return `${roundedDistance} ${distanceUnit}`
  }

  const renderSeeNearestDropOffPoints = () => {
    if (!isDropOffMapEnabled) return null

    const isEmptyState = !address || !nearbyDropOffPoints?.length

    const title = isEmptyState
      ? translate('drop_off_points.drop_off_points_title')
      : translate('drop_off_points.nearest_drop_off_point_title')

    const body =
      !isEmptyState &&
      translate('drop_off_points.drop_off_point_distance_note', {
        distance: getDistanceToNearestDropOffPoint(),
      })

    return (
      <Cell>
        <Bubble styling={Bubble.Styling.Tight}>
          <Cell
            type={Cell.Type.Navigating}
            prefix={<Icon name={LocationPinMap24} color={Icon.Color.GreyscaleLevel2} />}
            theme="transparent"
            title={title}
            body={body}
            onClick={onDropOffPointOpen}
            testId="generate-label-modal-see-drop-off-points"
          />
        </Bubble>
      </Cell>
    )
  }

  if (!isDropOffMapEnabled) return null
  if (isHomePickupSelected) return null

  return renderSeeNearestDropOffPoints()
}

export default DropOffPoints
